import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Send, Search, MessageCircle, Users, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import ChatList from '@/components/Messages/ChatList';
import messagesHeroBg from '@/assets/messages-hero-bg.jpg';

interface Profile {
  user_id: string;
  username: string;
  display_name: string | null;
  avatar_url: string | null;
}

interface Conversation {
  id: string;
  name: string | null;
  type: 'direct' | 'group';
  last_message_at: string | null;
  participants: {
    user_id: string;
    profiles: Profile;
  }[];
}

interface Message {
  id: string;
  content: string | null;
  sender_id: string;
  created_at: string;
  sender: Profile;
}

const Messages = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  
  // Set document title and meta description for SEO
  useEffect(() => {
    document.title = 'Messaggi - Chat Community Pet | AstroPet';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Chatta con altri proprietari di animali su AstroPet. Crea nuove conversazioni, condividi foto dei tuoi pet e connettiti con la community degli amanti degli animali.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Chatta con altri proprietari di animali su AstroPet. Crea nuove conversazioni, condividi foto dei tuoi pet e connettiti con la community degli amanti degli animali.';
      document.head.appendChild(meta);
    }
    
    // Set canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://astropet.it/messages');
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://astropet.it/messages';
      document.head.appendChild(link);
    }
  }, []);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);

  useEffect(() => {
    if (user) {
      fetchConversations();
      fetchUsers();
    }
  }, [user]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation);
    }
  }, [selectedConversation]);

  const fetchConversations = async () => {
    try {
      // First get conversations where user is a participant
      const { data: userConversations, error } = await supabase
        .from('conversation_participants')
        .select('conversation_id')
        .eq('user_id', user?.id)
        .is('left_at', null);

      if (error) throw error;

      const conversationIds = userConversations?.map(item => item.conversation_id) || [];

      if (conversationIds.length > 0) {
        // Get conversations details
        const { data: conversationsData, error: convError } = await supabase
          .from('conversations')
          .select('*')
          .in('id', conversationIds)
          .order('last_message_at', { ascending: false });

        if (convError) throw convError;

        // Get participants for each conversation
        const conversationsWithParticipants = await Promise.all(
          (conversationsData || []).map(async (conv) => {
            const { data: participantIds } = await supabase
              .from('conversation_participants')
              .select('user_id')
              .eq('conversation_id', conv.id)
              .is('left_at', null);

            const participants = await Promise.all(
              (participantIds || []).map(async (p) => {
                const { data: profile } = await supabase
                  .from('profiles')
                  .select('user_id, username, display_name, avatar_url')
                  .eq('user_id', p.user_id)
                  .single();

                return {
                  user_id: p.user_id,
                  profiles: profile || { user_id: '', username: '', display_name: null, avatar_url: null }
                };
              })
            );

            return {
              ...conv,
              type: conv.type as 'direct' | 'group',
              participants: participants
            };
          })
        );

        setConversations(conversationsWithParticipants);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare le chat",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, username, display_name, avatar_url')
        .neq('user_id', user?.id);

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchMessages = async (conversationId: string) => {
    setLoadingMessages(true);
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .eq('is_deleted', false)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Get sender profiles for each message
      const messagesWithSenders = await Promise.all(
        (data || []).map(async (msg) => {
          const { data: profile } = await supabase
            .from('profiles')
            .select('user_id, username, display_name, avatar_url')
            .eq('user_id', msg.sender_id)
            .single();

          return {
            ...msg,
            sender: profile || { user_id: '', username: '', display_name: null, avatar_url: null }
          };
        })
      );

      setMessages(messagesWithSenders);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast({
        title: "Errore",
        description: "Impossibile caricare i messaggi",
        variant: "destructive"
      });
    } finally {
      setLoadingMessages(false);
    }
  };

  const createConversation = async (otherUserId: string) => {
    try {
      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .insert({
          type: 'direct',
          created_by: user?.id
        })
        .select()
        .single();

      if (convError) throw convError;

      const { error: participantsError } = await supabase
        .from('conversation_participants')
        .insert([
          { conversation_id: conversation.id, user_id: user?.id },
          { conversation_id: conversation.id, user_id: otherUserId }
        ]);

      if (participantsError) throw participantsError;

      setSelectedConversation(conversation.id);
      setShowNewChat(false);
      fetchConversations();
      
      toast({
        title: "Chat creata",
        description: "Nuova conversazione avviata!"
      });
    } catch (error) {
      console.error('Error creating conversation:', error);
      toast({
        title: "Errore",
        description: "Impossibile creare la chat",
        variant: "destructive"
      });
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    try {
      const { error } = await supabase
        .from('messages')
        .insert({
          conversation_id: selectedConversation,
          sender_id: user?.id,
          content: newMessage.trim(),
          message_type: 'text'
        });

      if (error) throw error;

      setNewMessage('');
      fetchMessages(selectedConversation);
    } catch (error) {
      console.error('Error sending message:', error);
      toast({
        title: "Errore",
        description: "Impossibile inviare il messaggio",
        variant: "destructive"
      });
    }
  };


  const getConversationName = (conversation: Conversation) => {
    if (conversation.type === 'group') {
      return conversation.name || 'Chat di gruppo';
    }
    
    const otherParticipant = conversation.participants?.find(p => p.user_id !== user?.id);
    return otherParticipant?.profiles?.display_name || otherParticipant?.profiles?.username || 'Chat';
  };

  const getConversationAvatar = (conversation: Conversation) => {
    if (conversation.type === 'group') {
      return null;
    }
    
    const otherParticipant = conversation.participants?.find(p => p.user_id !== user?.id);
    return otherParticipant?.profiles?.avatar_url;
  };

  // Function to extract post ID from post URL
  const extractPostId = (content: string): string | null => {
    const postUrlRegex = /\/app\/post\/([a-f0-9-]+)/;
    const match = content.match(postUrlRegex);
    return match ? match[1] : null;
  };

  // Function to check if message is a shared post
  const isSharedPost = (content: string): boolean => {
    return content.includes('Dai un\'occhiata a questo post:') && content.includes('/app/post/');
  };

  return (
    <div 
      className="cosmic-background min-h-screen"
      style={{ backgroundImage: `url(${messagesHeroBg})` }}
    >
      <style>{`
        amp-auto-ads { display: none !important; }
        .adsbygoogle { display: none !important; }
      `}</style>
      <div className="max-w-4xl mx-auto p-4 pb-32 lg:pb-4">
      <div className="h-[calc(100vh-8rem)]">
        <ChatList
          conversations={conversations}
          selectedConversation={selectedConversation}
          currentUserId={user?.id || ''}
          loading={loading}
          showNewChat={showNewChat}
          searchQuery={searchQuery}
          users={users}
          onConversationSelect={(id) => window.location.href = `/app/chat/${id}`}
          onNewChatToggle={() => setShowNewChat(!showNewChat)}
          onSearchChange={setSearchQuery}
          onUserSelect={createConversation}
          getConversationName={getConversationName}
          getConversationAvatar={getConversationAvatar}
        />
      </div>
      </div>
    </div>
  );
};
export default Messages;